import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { graphql, useStaticQuery, navigate } from 'gatsby';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { GeoRegionContext } from '../../contexts/GeoRegionContext';
import {
    getDomainForCode,
    regionFriendlyNames,
    isRootLang,
} from '../../global/countries';
import { getColor } from '../../components/Global/DataUtils';
import CtaLink from '../Global/CtaLink';
import RichText from '../Global/RichText';
import * as styles from './geo-region-banner.module.css';
import {
    composeRegionRedirectUrl,
    replaceRegionPlaceholders,
    markGeoRegionChosen,
    withRegionChoice,
} from '../../utils/geoRegion';

import {
    KontentStringValue,
    KontentRichText,
    BrandColor,
} from '../../../types/KontentProps';
import { isRichTextFieldBlank } from '../../global/utility';

const DISMISS_ANIMATION_MS = 300;

interface GeoRegionBannerElements {
    banner_text?: KontentRichText;
    redirect_cta_label?: KontentStringValue;
    stay_cta_label?: KontentStringValue;
    banner_background_color?: BrandColor;
    banner_text_color?: BrandColor;
    redirect_cta_text_color?: BrandColor;
    redirect_cta_border_color?: BrandColor;
    redirect_cta_background_color?: BrandColor;
    redirect_cta_hover_text_color?: BrandColor;
    redirect_cta_hover_border_color?: BrandColor;
    redirect_cta_hover_background_color?: BrandColor;
    stay_cta_text_color?: BrandColor;
    stay_cta_border_color?: BrandColor;
    stay_cta_background_color?: BrandColor;
    stay_cta_hover_text_color?: BrandColor;
    stay_cta_hover_border_color?: BrandColor;
    stay_cta_hover_background_color?: BrandColor;
}

interface GeoRegionBannerNode {
    preferred_language: string;
    elements: GeoRegionBannerElements;
}

interface GeoRegionBannerQueryData {
    allKontentItemComponentGeoRegionBanner: {
        nodes: GeoRegionBannerNode[];
    };
}

const EMPTY_ELEMENTS: GeoRegionBannerElements = {};

/**
 * GeoRegionBanner
 * Fetches all geo-region banner entries via static query and picks
 * the one matching the current preferredLanguage (or 'default').
 * Renders only while GeoRegionContext holds a region suggestion.
 */
const GeoRegionBanner: React.FC = () => {
    const pageMetadata = useContext(PageMetadataContext);
    const { suggestion, clearSuggestion } = useContext(GeoRegionContext);

    const detectedRegionCode = suggestion?.code;
    const detectedRegionName = suggestion?.name;
    const availableCountries: string[] = pageMetadata?.availableCountries || [];
    const currentPreferredLanguage: string =
        pageMetadata?.preferredLanguage || 'default';
    const currentPagePath: string =
        pageMetadata?.pagePaths?.[pageMetadata?.slug ?? ''] || '/';

    const bannerRef = useRef<HTMLDivElement>(null);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isDismissing, setIsDismissing] = useState(false);
    // The banner stays mounted across navigation; reset once it has closed.
    useEffect(() => {
        if (!suggestion) setIsDismissing(false);
    }, [suggestion]);
    const currentRegionLabel =
        regionFriendlyNames[currentPreferredLanguage] || 'United States';
    const detectedRegionLabel = detectedRegionName || '';

    const bannerQueryData: GeoRegionBannerQueryData = useStaticQuery(graphql`
        query GeoRegionBannerQuery {
            allKontentItemComponentGeoRegionBanner {
                nodes {
                    preferred_language
                    elements {
                        banner_text {
                            value
                        }
                        redirect_cta_label {
                            value
                        }
                        stay_cta_label {
                            value
                        }

                        banner_background_color {
                            ...kontentColor
                        }
                        banner_text_color {
                            ...kontentColor
                        }

                        redirect_cta_text_color {
                            ...kontentColor
                        }
                        redirect_cta_border_color {
                            ...kontentColor
                        }
                        redirect_cta_background_color {
                            ...kontentColor
                        }

                        redirect_cta_hover_text_color {
                            ...kontentColor
                        }
                        redirect_cta_hover_border_color {
                            ...kontentColor
                        }
                        redirect_cta_hover_background_color {
                            ...kontentColor
                        }

                        stay_cta_text_color {
                            ...kontentColor
                        }
                        stay_cta_border_color {
                            ...kontentColor
                        }
                        stay_cta_background_color {
                            ...kontentColor
                        }

                        stay_cta_hover_text_color {
                            ...kontentColor
                        }
                        stay_cta_hover_border_color {
                            ...kontentColor
                        }
                        stay_cta_hover_background_color {
                            ...kontentColor
                        }
                    }
                }
            }
        }
    `);

    const banners: GeoRegionBannerNode[] =
        bannerQueryData?.allKontentItemComponentGeoRegionBanner?.nodes || [];

    const preferredLanguageLower = String(
        currentPreferredLanguage || 'default'
    ).toLowerCase();

    const matchedBanner =
        banners.find(
            (banner) =>
                String(banner?.preferred_language || '').toLowerCase() ===
                preferredLanguageLower
        ) ||
        banners.find(
            (banner) =>
                String(banner?.preferred_language || '').toLowerCase() ===
                'default'
        );

    const bannerElements: GeoRegionBannerElements =
        matchedBanner?.elements ?? EMPTY_ELEMENTS;

    const shouldRender =
        !!detectedRegionCode &&
        !!detectedRegionName &&
        Object.keys(bannerElements).length > 0;

    const bannerBackgroundColor = shouldRender
        ? getColor(bannerElements.banner_background_color) || '#fff8e6'
        : undefined;
    const bannerTextColor = shouldRender
        ? getColor(bannerElements.banner_text_color) || '#000'
        : undefined;

    const redirectCtaTextColor = shouldRender
        ? getColor(bannerElements.redirect_cta_text_color) || undefined
        : undefined;
    const redirectCtaBorderColor = shouldRender
        ? getColor(bannerElements.redirect_cta_border_color) || undefined
        : undefined;
    const redirectCtaBackgroundColor = shouldRender
        ? getColor(bannerElements.redirect_cta_background_color) || undefined
        : undefined;

    const defaultHoverText = 'var(--color-orange)';
    const defaultHoverBorder = 'var(--color-white)';
    const defaultHoverBg = 'var(--color-white)';

    const redirectCtaHoverTextColor = shouldRender
        ? getColor(bannerElements.redirect_cta_hover_text_color) ||
          defaultHoverText
        : undefined;
    const redirectCtaHoverBorderColor = shouldRender
        ? getColor(bannerElements.redirect_cta_hover_border_color) ||
          defaultHoverBorder
        : undefined;
    const redirectCtaHoverBackgroundColor = shouldRender
        ? getColor(bannerElements.redirect_cta_hover_background_color) ||
          defaultHoverBg
        : undefined;

    const stayCtaTextColor = shouldRender
        ? getColor(bannerElements.stay_cta_text_color) || undefined
        : undefined;
    const stayCtaBorderColor = shouldRender
        ? getColor(bannerElements.stay_cta_border_color) || undefined
        : undefined;
    const stayCtaBackgroundColor = shouldRender
        ? getColor(bannerElements.stay_cta_background_color) || undefined
        : undefined;

    const stayCtaHoverTextColor = shouldRender
        ? getColor(bannerElements.stay_cta_hover_text_color) || defaultHoverText
        : undefined;
    const stayCtaHoverBorderColor = shouldRender
        ? getColor(bannerElements.stay_cta_hover_border_color) ||
          defaultHoverBorder
        : undefined;
    const stayCtaHoverBackgroundColor = shouldRender
        ? getColor(bannerElements.stay_cta_hover_background_color) ||
          defaultHoverBg
        : undefined;

    const rawBannerText: string = shouldRender
        ? (!isRichTextFieldBlank(bannerElements.banner_text?.value)
              ? bannerElements.banner_text?.value
              : `You are currently viewing the ${currentRegionLabel} site.`) ||
          ''
        : '';

    const parsedBannerText = shouldRender
        ? replaceRegionPlaceholders(
              rawBannerText,
              currentRegionLabel,
              detectedRegionLabel
          )
        : '';

    const redirectCtaLabel = shouldRender
        ? (bannerElements.redirect_cta_label &&
              bannerElements.redirect_cta_label.value.replace(
                  '{user_region}',
                  detectedRegionLabel
              )) ||
          `Go to the ${detectedRegionLabel} site`
        : '';

    const stayCtaLabel = shouldRender
        ? (bannerElements.stay_cta_label &&
              bannerElements.stay_cta_label.value) ||
          'Stay on this site'
        : '';

    const pageExistsInDetectedRegion =
        shouldRender &&
        Array.isArray(availableCountries) &&
        availableCountries.includes(detectedRegionCode!);

    const detectedRegionDomain = shouldRender
        ? getDomainForCode(detectedRegionCode)
        : '';
    const detectedRegionIsRoot = shouldRender
        ? isRootLang(detectedRegionCode)
        : false;

    const targetPath = useMemo(() => {
        if (!shouldRender) return '/';
        return pageExistsInDetectedRegion
            ? detectedRegionIsRoot
                ? currentPagePath
                : `/${detectedRegionCode}${currentPagePath}`
            : '/';
    }, [
        shouldRender,
        pageExistsInDetectedRegion,
        detectedRegionIsRoot,
        detectedRegionCode,
        currentPagePath,
    ]);

    const progressiveHref = useMemo(() => {
        if (!shouldRender || !detectedRegionCode) return '#';
        return withRegionChoice(
            composeRegionRedirectUrl(detectedRegionCode, targetPath),
            detectedRegionCode,
            typeof window === 'undefined' ? '' : window.location.origin
        );
    }, [shouldRender, detectedRegionCode, targetPath]);

    const handleRedirectClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        markGeoRegionChosen(detectedRegionCode);

        if (typeof window === 'undefined') return;

        try {
            const destinationOrigin = new URL(detectedRegionDomain).origin;
            const currentOrigin = window.location.origin;

            if (currentOrigin === destinationOrigin) {
                clearSuggestion();
                navigate(targetPath);
            } else {
                window.location.href = progressiveHref;
            }
        } catch (err) {
            window.location.href = progressiveHref;
        }
    };

    const dismissBanner = () => {
        markGeoRegionChosen();
        setIsDismissing(true);

        dismissTimerRef.current = setTimeout(() => {
            dismissTimerRef.current = null;
            clearSuggestion();
        }, DISMISS_ANIMATION_MS);
    };

    const handleStayClick = (
        e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
    ) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        dismissBanner();
    };

    useEffect(() => {
        if (typeof document === 'undefined') return;

        const setOffset = (px: string) =>
            document.documentElement.style.setProperty(
                '--geo-banner-offset',
                px
            );

        if (!shouldRender) {
            setOffset('0px');
            return;
        }

        const bannerElement = bannerRef.current;
        if (!bannerElement) {
            setOffset('0px');
            return;
        }

        const reportHeight = () => setOffset(`${bannerElement.offsetHeight}px`);
        reportHeight();

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(reportHeight);
            resizeObserver.observe(bannerElement);
        }

        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = null;
            }
            setOffset('0px');
        };
    }, [shouldRender, parsedBannerText, bannerElements, detectedRegionName]);

    const renderedBannerText = RichText(
        {
            ...(bannerElements.banner_text || {}),
            value: parsedBannerText,
        },
        undefined,
        undefined,
        undefined,
        'banner_text'
    );

    if (!shouldRender) {
        return null;
    }

    return (
        <div
            id="geo-region-banner"
            className={`${styles.banner} ${
                isDismissing ? styles.bannerDismissing : ''
            }`}
            role="region"
            aria-label="Region suggestion"
            ref={bannerRef}
            style={{
                backgroundColor: bannerBackgroundColor,
                color: bannerTextColor,
            }}
            data-geo-banner="true"
        >
            <div className={styles.text}>{renderedBannerText}</div>

            <div className={styles.actions}>
                <CtaLink
                    href={progressiveHref}
                    preserveCountrySelection
                    onClick={handleRedirectClick}
                    color={redirectCtaTextColor}
                    borderColor={redirectCtaBorderColor}
                    backgroundColor={redirectCtaBackgroundColor}
                    hoverBorderColor={redirectCtaHoverBorderColor}
                    hoverFontColor={redirectCtaHoverTextColor}
                    hoverBgColor={redirectCtaHoverBackgroundColor}
                    visualStyle="outlineLight"
                    elementCodename="redirect_cta_label"
                >
                    {redirectCtaLabel}
                </CtaLink>

                <CtaLink
                    href="#"
                    onClick={handleStayClick}
                    color={stayCtaTextColor}
                    borderColor={stayCtaBorderColor}
                    backgroundColor={stayCtaBackgroundColor}
                    hoverBorderColor={stayCtaHoverBorderColor}
                    hoverFontColor={stayCtaHoverTextColor}
                    hoverBgColor={stayCtaHoverBackgroundColor}
                    visualStyle="outlineLight"
                    elementCodename="stay_cta_label"
                >
                    {stayCtaLabel}
                </CtaLink>
            </div>

            <button
                className={styles.closeButton}
                onClick={handleStayClick}
                aria-label="Dismiss banner"
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M18 6L6 18M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

export default GeoRegionBanner;
